import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  CheckCircle, 
  AlertCircle, 
  Loader2,
  Clock,
  Shield,
  ChevronRight,
  Star,
  Zap,
  Phone,
  Mail
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PHSQuoteMultiOptionPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [actionComplete, setActionComplete] = useState(null);

  useEffect(() => {
    if (token) {
      loadQuote();
    } else {
      setError('Invalid quote link');
      setLoading(false);
    }
  }, [token]);

  const loadQuote = async () => {
    try {
      // Try multi-option first, fall back to single
      let res;
      try {
        res = await axios.get(`${API}/api/phs/public/quote/multi?token=${token}`);
      } catch {
        res = await axios.get(`${API}/api/phs/members/public/quote?token=${token}`);
        // Convert single to multi format for display
        if (res.data && !res.data.options) {
          res.data = {
            ...res.data,
            quote_mode: 'single_option',
            options: [{
              option_id: 'A',
              title: 'Repair Solution',
              description: res.data.description || '',
              price: res.data.total,
              lead_time: res.data.lead_time,
              warranty_period: res.data.warranty,
              selected: false
            }]
          };
        }
      }
      
      setQuote(res.data);
      
      // Pre-select if only one option
      if (res.data?.options?.length === 1) {
        setSelectedOption('A');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load quote');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOption = async (optionId) => {
    setSelectedOption(optionId);
    
    if (quote?.quote_mode === 'multi_option') {
      try {
        await axios.post(`${API}/api/phs/public/quote/select-option?token=${token}`, {
          option_id: optionId
        });
      } catch (err) {
        console.error('Failed to save selection:', err);
      }
    }
  };

  const handleAccept = async () => {
    if (!selectedOption) {
      setError('Please select an option before accepting');
      return;
    }
    
    setAccepting(true);
    setError(null);
    
    try {
      let res;
      if (quote?.quote_mode === 'multi_option') {
        res = await axios.post(`${API}/api/phs/public/quote/accept-multi?token=${token}`);
      } else {
        res = await axios.post(`${API}/api/phs/members/public/quote/accept?token=${token}`);
      }
      
      setActionComplete('accepted');
      
      // Redirect to payment
      setTimeout(() => {
        if (res.data.payment_url) {
          navigate(res.data.payment_url);
        } else if (res.data.invoice_token) {
          navigate(`/pay-invoice?token=${res.data.invoice_token}`);
        }
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to accept quote');
      setAccepting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getBadgeIcon = (badge) => {
    if (!badge) return null;
    if (badge.toLowerCase().includes('value') || badge.toLowerCase().includes('best')) {
      return <Star className="h-3 w-3" />;
    }
    if (badge.toLowerCase().includes('fast') || badge.toLowerCase().includes('priority')) {
      return <Zap className="h-3 w-3" />;
    }
    return <CheckCircle className="h-3 w-3" />;
  };

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/30 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#0F766E] mx-auto mb-4" />
          <p className="text-gray-600">Loading your options...</p>
        </div>
      </div>
    );
  }

  // Error
  if (error && !quote) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/30 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl border-0">
          <CardContent className="pt-8 text-center">
            <AlertCircle className="h-16 w-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Quote Unavailable</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-y-2 text-sm">
              <a href="tel:08001234567" className="flex items-center justify-center gap-2 text-[#0F766E]">
                <Phone className="h-4 w-4" /> 0800 123 4567
              </a>
              <a href="mailto:support@parkhomesupport.co.uk" className="flex items-center justify-center gap-2 text-[#0F766E]">
                <Mail className="h-4 w-4" /> support@parkhomesupport.co.uk
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Accepted
  if (actionComplete === 'accepted') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/30 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl border-0">
          <CardContent className="pt-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Option Selected!</h2>
            <p className="text-gray-600 mb-4">Redirecting you to complete payment...</p>
            <Loader2 className="h-6 w-6 animate-spin text-[#0F766E] mx-auto" />
          </CardContent>
        </Card>
      </div>
    );
  }

  const selectedOptionData = quote?.options?.find(o => o.option_id === selectedOption);

  // Main Multi-Option View
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/30 to-slate-100 py-8 px-4" data-testid="quote-multi-option-page">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8 text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-[#0F766E] to-[#115E59] rounded-2xl flex items-center justify-center shadow-lg">
          <img src="/phs-logo.png" alt="PHS" className="w-12 h-12 object-contain" onError={(e) => e.target.style.display = 'none'} />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Park Home Support</h1>
        <p className="text-gray-500">Your Curated Repair Options</p>
      </div>

      {/* Quote Info */}
      <div className="max-w-4xl mx-auto mb-6">
        <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-600">
          <span>Quote: <span className="font-mono font-medium">{quote?.quote_id}</span></span>
          <span className="text-gray-300">•</span>
          <span>For: <span className="font-medium">{quote?.member_name}</span></span>
          <span className="text-gray-300">•</span>
          <span className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            Valid until {formatDate(quote?.valid_until)}
          </span>
        </div>
      </div>

      {/* Options Grid */}
      <div className="max-w-4xl mx-auto mb-8">
        <div className={`grid gap-6 ${quote?.options?.length === 2 ? 'md:grid-cols-2' : 'max-w-lg mx-auto'}`}>
          {quote?.options?.map((option) => (
            <Card 
              key={option.option_id}
              className={`relative cursor-pointer transition-all duration-200 hover:shadow-xl border-2 ${
                selectedOption === option.option_id 
                  ? 'border-[#0F766E] ring-2 ring-[#0F766E]/20 shadow-lg' 
                  : 'border-gray-200 hover:border-[#0F766E]/50'
              }`}
              onClick={() => handleSelectOption(option.option_id)}
              data-testid={`option-card-${option.option_id}`}
            >
              {/* Badge */}
              {option.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-[#0F766E] text-white px-4 py-1 flex items-center gap-1 shadow">
                    {getBadgeIcon(option.badge)}
                    {option.badge}
                  </Badge>
                </div>
              )}

              {/* Selection Indicator */}
              {selectedOption === option.option_id && (
                <div className="absolute top-4 right-4">
                  <div className="w-8 h-8 bg-[#0F766E] rounded-full flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                </div>
              )}

              <CardHeader className="pb-4 pt-8">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#0F766E]/10 rounded-xl flex items-center justify-center text-[#0F766E] font-bold text-xl">
                    {option.option_id}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{option.title}</CardTitle>
                    {option.description && (
                      <p className="text-sm text-gray-500 mt-1">{option.description}</p>
                    )}
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Price */}
                <div className="p-4 bg-gradient-to-br from-[#0F766E]/10 to-[#0F766E]/5 rounded-xl text-center">
                  <p className="text-sm text-gray-600 mb-1">Total Price</p>
                  <p className="text-4xl font-bold text-[#0F766E]">
                    £{(option.price || 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Including VAT where applicable</p>
                </div>

                {/* Details */}
                <div className="space-y-2">
                  {option.lead_time && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-600 flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Lead Time
                      </span>
                      <span className="font-medium text-gray-900">{option.lead_time}</span>
                    </div>
                  )}
                  {option.warranty_period && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm text-gray-600 flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        Warranty
                      </span>
                      <span className="font-medium text-gray-900">{option.warranty_period}</span>
                    </div>
                  )}
                </div>

                {/* Select Button */}
                <Button 
                  variant={selectedOption === option.option_id ? "default" : "outline"}
                  className={`w-full ${
                    selectedOption === option.option_id 
                      ? 'bg-[#0F766E] hover:bg-[#115E59]' 
                      : 'border-[#0F766E] text-[#0F766E] hover:bg-[#0F766E]/10'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectOption(option.option_id);
                  }}
                >
                  {selectedOption === option.option_id ? (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Selected
                    </>
                  ) : (
                    'Select This Option'
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Notes */}
      {quote?.client_notes && (
        <div className="max-w-4xl mx-auto mb-6">
          <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">Note: </span>
              {quote.client_notes}
            </p>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="max-w-4xl mx-auto mb-6">
          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <p className="text-sm text-red-600 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {error}
            </p>
          </div>
        </div>
      )}

      {/* Accept Section */}
      <div className="max-w-md mx-auto">
        <Card className="shadow-xl border-0">
          <CardContent className="p-6 space-y-4">
            {selectedOptionData ? (
              <>
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-1">You selected</p>
                  <p className="font-semibold text-lg">{selectedOptionData.title}</p>
                  <p className="text-3xl font-bold text-[#0F766E] mt-2">
                    £{(selectedOptionData.price || 0).toFixed(2)}
                  </p>
                </div>

                <Button 
                  onClick={handleAccept}
                  disabled={accepting}
                  className="w-full h-14 text-lg bg-[#0F766E] hover:bg-[#115E59] shadow-lg"
                  data-testid="accept-quote-btn"
                >
                  {accepting ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Accept & Proceed to Payment
                      <ChevronRight className="h-5 w-5 ml-2" />
                    </>
                  )}
                </Button>
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-gray-600">Please select an option above to continue</p>
              </div>
            )}

            <div className="flex items-center justify-center gap-4 pt-4 border-t text-sm text-gray-500">
              <div className="flex items-center gap-1">
                <Shield className="h-4 w-4 text-green-600" />
                Secure Payment
              </div>
              <div className="flex items-center gap-1">
                <CheckCircle className="h-4 w-4 text-green-600" />
                No Hidden Fees
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <div className="max-w-4xl mx-auto mt-8 text-center text-sm text-gray-500">
        <p>Questions? Contact us at <a href="mailto:support@parkhomesupport.co.uk" className="text-[#0F766E] hover:underline">support@parkhomesupport.co.uk</a></p>
        <p className="mt-2">© {new Date().getFullYear()} Park Home Support. All rights reserved.</p>
      </div>
    </div>
  );
}
